import React from 'react';
import { Navigate } from 'react-router-dom';

import { permissions, withRouter, lang, scssVariables as variables, remCss } from 'src/helpers';
import { GENERAL_ICONS } from 'src/components/svg';

import BaseComponent from 'src/components/BaseComponent';
import MainLayout from 'src/components/MainLayout';
import constants from 'src/constants';

import Messages from 'src/pages/main/Messages';
import model from 'src/pages/main/model';

import Treatments from './Treatments';
import Appointment from './Appointment';
import Doctors from './Doctors';
import AddDoctor from './Doctors/Invite';
import EditDoctor from './Doctors/Edit';
import Doctor from './Doctor';
import Discovery from './Discovery';
import Schedule from './Schedule';
import Stats from './Stats';
import Patients from './Patients';
import PatientsAnalyse from './Patients/Analyses';
import Journey from './Journey';
import Invoices from './Invoices';
import AddPatient from './Patients/Create';
import PatientDetail from './Patients/Detail';
import TreatmentDetail from './Treatments/TreatmentDetail';
import AppointmentDetail from './Appointment/Detail';
import AppointmentBook from './Appointment/Book';
import Payment from './Invoices/Payment';
import Analyses from './Analyses';
import Manager from './Manager';
import ManagerLocations from './Manager/Locations';
import EditAppointment from './Appointment/Edit';
import settings_pages from './Settings';
import DoctorStats from './DoctorStats';
import LANG_CONST from 'src/helpers/lang/const';
import AddManager from './Manager/Add';
import AlertManager from 'src/components/alert/AlertManager';
import CreateTreatment from './Treatments/Create';
import ClinicStats from './ClinicStats';
import EditPatient from './Patients/Edit';

const style = {};

style.backgroundColor = variables['colors-mc-white'];

class Main extends BaseComponent {
  componentDidMount() {
    model.subscribe(this.subscription);
    model.foxsocket().connect();
    model.on('calling', this.onCalling);
    model.on('router_navigate', this.routerNavigate);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
    model.foxsocket().disconnect();
    model.cancel('calling', this.onCalling);
    model.cancel('router_navigate', this.routerNavigate);
  }

  routerNavigate = ({ url, params }) => {
    this.navigate(url, params);
  };

  onCalling = (partnerId) => {
    if (partnerId > 0) this.navigate(permissions.PAGES.MESSAGES_USER_PAGE.match(partnerId));
  };

  componentDidUpdate(prevProps) {
    if (prevProps.router?.location.pathname !== this.props.router?.location.pathname) {
      model.emit('hide_back');
    }
  }

  render() {
    const user = model.getUser();
    const nameSearch = this.props.router.location.pathname + ':query';
    const isSettings = this.props.isSettings;

    return (
      <MainLayout
        currency={user?.currency}
        isSettings={isSettings}
        drawMenu={permissions.ACTIVE_PAGE_LIST.filter((p) =>
          isSettings ? p.path.includes('setting') && !!p.title : p.isDrawMenu
        )}
        dropdownMenu={permissions.ACTIVE_PAGE_LIST.filter(
          (p) => p.path.includes('settings') && !!p.title
        )}
        querySearch={model.input(nameSearch)}
        onQuerySearchChange={(v) => {
          model.handleChange(nameSearch, v);
          model.emit(nameSearch, v);
        }}
        user={user}
        showBack={model.state.showBack}
        funcBack={model.state.funcBack}
        logout={() => model.emit('logout')}
      >
        {this.props.children}
      </MainLayout>
    );
  }
}

const MainContainer = withRouter(Main);

const doctors_pages = {
  DOCTORS_DETAIL_PAGE: {
    name: 'doctors_detail_page',
    path: '/doctors/:username',
    match: (username) => '/doctors/' + username,
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <Doctor />
      </MainContainer>
    )
  },
  DOCTORS_ADD_PAGE: {
    name: 'doctors_add_page',
    path: '/doctors/add/:id?',
    match: (id = '') => `/doctors/add/${id}`,
    checkActive: ({ auth }) => {
      const isActive =
        !!auth &&
        (auth.isClinic || auth.isManager) &&
        auth.status !== constants.general.userStatus.disabled;
      if (!isActive) return false;

      const isOwner = auth.managerToClinic === auth.id;
      const isAdmin = !!auth.managerAdmin;
      const hospitalIds = auth.managerAccess?.filter((a) => !!a.doctors);

      return isOwner || isAdmin || (hospitalIds && hospitalIds.length > 0);
    },
    element: (
      <MainContainer>
        <AddDoctor />
      </MainContainer>
    )
  },
  DOCTORS_EDIT_PAGE: {
    name: 'doctors_edit_pages',
    path: '/doctors/edit/:id',
    match: (id) => `/doctors/edit/${id}`,
    checkActive: ({ auth, doctor }) => {
      const isOwner = auth.managerToClinic === auth.id;
      const isAdmin = !!auth.managerAdmin;
      const hospitalIds = auth.managerAccess?.filter((a) => !!a.doctors).map((a) => a.hospitalId);

      return (
        !!auth &&
        (auth.isClinic || auth.isManager) &&
        auth.status !== constants.general.userStatus.disabled &&
        (isOwner ||
          isAdmin ||
          !doctor ||
          ((doctor?.hospitalIds || []).length === 0 && hospitalIds?.length > 0) ||
          (doctor?.hospitalIds || [])?.find((h) => hospitalIds?.includes(h)))
      );
    },
    element: (
      <MainContainer>
        <EditDoctor />
      </MainContainer>
    )
  },
  DOCTORS_PAGE: {
    name: 'doctors_page',
    path: '/doctors',
    title: 'Doctors',
    icon: 'doctor-icon',
    isDrawMenu: true,
    checkActive: ({ auth }) => !!auth && (auth.isClinic || auth.isManager),
    element: (
      <MainContainer>
        <Doctors />
      </MainContainer>
    )
  }
};

const treatments_pages = {
  ANALYSES_PAGE: {
    name: 'analyses_page',
    path: '/analyses',
    title: LANG_CONST.titles.analyses,
    icon: 'treatment-icon',
    checkActive: ({ auth }) => !!auth && auth.isPatient,
    isDrawMenu: true,
    isActiveEx: () => window.location.href.includes('/treatments'),
    element: (
      <MainContainer>
        <Analyses />
      </MainContainer>
    )
  },
  CREATE_TREATMENTS_PAGE: {
    name: 'CREATE_TREATMENTS_PAGE',
    path: '/treatments/create/:patientId',
    match: (patientId) => '/treatments/create/' + patientId,
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <CreateTreatment />
      </MainContainer>
    )
  },
  TREATMENTS_DETAIL_PAGE: {
    name: 'treatments_detail_page',
    path: '/treatments/:treatmentId',
    match: (treatmentId) => '/treatments/' + treatmentId,
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <TreatmentDetail />
      </MainContainer>
    )
  },
  TREATMENTS_PAGE: {
    name: 'treatments_page',
    path: '/treatments',
    icon: 'treatment-icon',
    title: LANG_CONST.titles.treatments,
    isDrawMenu: true,
    checkActive: ({ auth }) => !!auth && (auth.isDoctor || auth.isClinic || auth.isManager),
    beforeClick: ({ auth }) => {
      if (auth.isDoctor) return true;
      const isOwner = auth.managerToClinic === auth.id;
      if (isOwner) return true;

      const isAdmin = !!auth.managerAdmin;
      const hospitalIds = auth.managerAccess
        ?.filter((a) => !!a.treatments)
        .map((a) => a.hospitalId);

      if (isAdmin || (hospitalIds && hospitalIds.length > 0)) {
        return true;
      } else {
        AlertManager.popup(AlertManager.TYPES.INFO, {
          maxWidth: remCss(600),
          title: LANG_CONST.manager_no_permissions.treatments,
          actionButton: {
            label: LANG_CONST.buttons.ok,
            action: ({ hide }) => hide()
          }
        });
        return false;
      }
    },
    element: (
      <MainContainer>
        <Treatments />
      </MainContainer>
    )
  }
};

const messages_pages = {
  MESSAGES_PAGE: {
    name: 'messages_page',
    path: '/messages',
    icon: 'message-icon',
    title: 'Messages',
    isDrawMenu: true,
    // checkActive: ({ auth }) => !!auth && (auth.isPatient || auth.isDoctor),
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <Messages />
      </MainContainer>
    )
  },
  MESSAGES_USER_PAGE: {
    name: 'messages_user_page',
    path: '/messages/:id',
    match: (id) => '/messages/' + id,
    // checkActive: ({ auth }) => !!auth && (auth.isPatient || auth.isDoctor),
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <Messages />
      </MainContainer>
    )
  }
};

const appointments_pages = {
  APPOINTMENTS_BOOK_PAGE: {
    name: 'appointment_book_page',
    path: '/doctors/:username/book',
    match: (username) => `/doctors/${username}/book`,
    checkActive: ({ auth }) => !!auth && auth.isPatient,
    element: (
      <MainContainer>
        <AppointmentBook />
      </MainContainer>
    )
  },
  APPOINTMENTS_PAGE: {
    name: 'appointments_page',
    path: '/appointments',
    title: 'Appointments',
    icon: 'appointment-icon',
    isDrawMenu: true,
    isActiveEx: () =>
      window.location.href.includes('/doctors') &&
      (window.location.href.includes('/book') || window.location.href.includes('/reschedule')),
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <Appointment />
      </MainContainer>
    )
  },
  APPOINTMENTS_DETAIL_PAGE: {
    name: 'appointments_detail_page',
    path: '/appointments/:id',
    match: (id) => `/appointments/${id}`,
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <AppointmentDetail />
      </MainContainer>
    )
  },
  APPOINTMENTS_RESCHEDULE_PAGE: {
    name: 'appointments_reschedule_page',
    path: '/appointments/:rescheduleId/reschedule',
    match: (rescheduleId) => `/appointments/${rescheduleId}/reschedule`,
    checkActive: (params) =>
      !params.appointment || permissions.ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.checkActive(params),
    element: (
      <MainContainer>
        <AppointmentBook />
      </MainContainer>
    )
  },
  CLINIC_APPOINTMENTS_REBOOK_PAGE: {
    name: 'CLINIC_APPOINTMENTS_REBOOK_PAGE',
    path: '/appointments/:rebookId/rebook',
    match: (rebookId) => `/appointments/${rebookId}/rebook`,
    checkActive: (params) =>
      !params.appointment || permissions.ACTIONS.APPOINTMENTS_REBOOK_BUTTON.checkActive(params),
    element: (
      <MainContainer>
        <AppointmentBook />
      </MainContainer>
    )
  },
  APPOINTMENTS_CREATE_PAGE: {
    name: 'APPOINTMENTS_CREATE_PAGE',
    path: '/appointments/book',
    checkActive: ({ auth }) => {
      return (
        !auth?.isPatient &&
        auth.status !== constants.general.userStatus.disabled &&
        (auth.isDoctor ||
          auth.isClinic ||
          auth.managerAdmin ||
          auth.managerAccess?.filter((i) => !!i.appointments).length > 0)
      );
    },
    element: (
      <MainContainer>
        <AppointmentBook />
      </MainContainer>
    )
  },
  APPOINTMENTS_EDIT_PAGE: {
    name: 'appointments_edit_page',
    path: '/appointments/:id/edit',
    match: (id) => `/appointments/${id}/edit`,
    checkActive: (params) =>
      !params.appointment || permissions.ACTIONS.APPOINTMENTS_EDIT_BUTTON.checkActive(params),
    element: (
      <MainContainer>
        <EditAppointment />
      </MainContainer>
    )
  }
};

const patients_page = {
  PATIENTS_PAGE: {
    name: 'patients_page',
    path: '/patients',
    title: 'Patients',
    icon: 'menu-patient-icon',
    isDrawMenu: true,
    checkActive: ({ auth }) => !!auth && (auth.isDoctor || auth.isClinic),
    element: (
      <MainContainer>
        <Patients />
      </MainContainer>
    )
  },
  PATIENTS_ADD_PAGE: {
    name: 'patients_add_page',
    path: '/patients/add/:tab?',
    match: (tab = '') => `/patients/add${tab ? `/${tab}` : ''}`,
    checkActive: ({ auth }) =>
      !!auth &&
      (auth.isDoctor || auth.isClinic) &&
      auth.status !== constants.general.userStatus.disabled,
    element: (
      <MainContainer>
        <AddPatient />
      </MainContainer>
    )
  },
  PATIENTS_EDIT_PAGE: {
    name: 'patients_edit_page',
    path: '/patients/edit/:id',
    match: (id) => `/patients/edit/${id}`,
    checkActive: ({ auth }) =>
      !!auth &&
      (auth.isDoctor || auth.isClinic) &&
      auth.status !== constants.general.userStatus.disabled,
    element: (
      <MainContainer>
        <EditPatient />
      </MainContainer>
    )
  },
  PATIENTS_ANALYSE_PAGE: {
    name: 'PATIENTS_ANALYSE_PAGE',
    path: '/patients/:id/analyse',
    match: (id) => `/patients/${id}/analyse`,
    checkActive: ({ auth }) =>
      !!auth && auth.isClinic && auth.status !== constants.general.userStatus.disabled,
    element: (
      <MainContainer>
        <PatientsAnalyse />
      </MainContainer>
    )
  },
  PATIENTS_DETAIL_PAGE: {
    name: 'patient_detail',
    path: '/patients/:id/:tab?',
    match: (id, tab = '') => `/patients/${id}${tab ? `/${tab}` : ''}`,
    checkActive: ({ auth }) => !!auth,
    element: (
      <MainContainer>
        <PatientDetail />
      </MainContainer>
    )
  },
  PATIENTS_APPOINTMENT_BOOK_PAGE: {
    name: 'patient_detail_book',
    path: '/patients/:patientId/appointments/book',
    match: (patientId) => `/patients/${patientId}/appointments/book`,
    checkActive: ({ auth }) =>
      !!auth &&
      (auth.isDoctor || auth.isClinic) &&
      auth.status !== constants.general.userStatus.disabled,
    element: (
      <MainContainer>
        <AppointmentBook />
      </MainContainer>
    )
  },
  PATIENTS_DETAIL_TREATMENT_DETAIL: {
    name: 'patient_detail_treatment_detail',
    path: '/patients/:id/treatments/:treatmentId',
    match: (id, treatmentId) => `/patients/${id}/treatments/${treatmentId}`,
    checkActive: ({ auth }) => !!auth && (auth.isDoctor || auth.isClinic),
    element: (
      <MainContainer>
        <TreatmentDetail />
      </MainContainer>
    )
  }
};

const pages = {
  ...doctors_pages,
  ...treatments_pages,
  ...messages_pages,
  ...appointments_pages,
  DISCOVERY_PAGE: {
    name: 'discovery_pages',
    path: '/discovery',
    icon: 'discovery-icon',
    title: LANG_CONST.titles.discovery,
    isDrawMenu: true,
    isActiveEx: () =>
      window.location.href.includes('/doctors') &&
      !window.location.href.includes('/book') &&
      !window.location.href.includes('/schedule'),
    checkActive: ({ auth }) => !!auth && auth.isPatient,
    element: (
      <MainContainer>
        <Discovery />
      </MainContainer>
    )
  },
  SCHEDULE_PAGE: {
    name: 'schedule_page',
    path: '/schedule',
    icon: 'schedule-icon',
    isDrawMenu: true,
    title: (auth) => lang.t(`${!auth.isDoctor ? `` : `My `}Schedule`),
    checkActive: ({ auth }) => !!auth && !auth.isPatient,
    element: (
      <MainContainer>
        <Schedule />
      </MainContainer>
    )
  },
  STATS_PAGE: {
    name: 'stats_page',
    path: '/stats',
    title: 'My Stats',
    icon: 'chart-icon',
    isDrawMenu: true,
    checkActive: ({ auth }) => !!auth && auth.isDoctor,
    element: (
      <MainContainer>
        <Stats />
      </MainContainer>
    )
  },
  DOCTORSTATS_PAGE: {
    name: 'doctorstats_page',
    path: '/drstats',
    title: 'Stats',
    icon: 'manager-icon',
    isDrawMenu: true,
    checkActive: ({ auth }) => !!auth && auth.isDoctor,
    element: (
      <MainContainer>
        <DoctorStats />
      </MainContainer>
    )
  },
  CLINICSTATS_PAGE: {
    name: 'CLINICSTATS_PAGE',
    path: '/allstats',
    title: 'Stats',
    icon: 'manager-icon',
    isDrawMenu: true,
    checkActive: ({ auth }) => !!auth && auth.isClinic,
    element: (
      <MainContainer>
        <ClinicStats />
      </MainContainer>
    )
  },

  ...patients_page,
  INVOICES_PAYMENT_PAGE: {
    name: 'invoices_payment_page',
    path: '/invoices/payment/:invoiceId',
    match: (invoiceId) => `/invoices/payment/${invoiceId}`,
    checkActive: ({ auth }) => !!auth && auth.isPatient,
    element: (
      <MainContainer>
        <Payment />
      </MainContainer>
    )
  },
  INVOICES_PAGE: {
    name: 'invoices_page',
    path: '/invoices',
    isDrawMenu: true,
    icon: 'invoice-icon',
    title: 'Invoices',
    checkActive: ({ auth }) => !!auth && !auth.isManager,
    element: (
      <MainContainer>
        <Invoices />
      </MainContainer>
    )
  },
  JOURNEY_PAGE: {
    name: 'journey_page',
    path: '/journey/:journeyId',
    navPath: '/journey/timeline',
    match: (journeyId) => `/journey/${journeyId}`,
    icon: 'journey-icon',
    title: 'Diary',
    isDrawMenu: true,
    isActiveEx: () => window.location.href.includes('/journey'),
    checkActive: ({ auth }) => !!auth && auth.isPatient,
    element: (
      <MainContainer>
        <Journey />
      </MainContainer>
    )
  },
  JOURNEYS_REDIRECT: {
    name: 'journey_redirect',
    path: '/journey',
    checkActive: ({ auth }) => !!auth && auth.isPatient,
    element: <Navigate to={'/journey/timeline'} />
  },
  ...settings_pages,
  MANAGERS_PAGE: {
    name: 'managers_page',
    path: '/managers',
    isDrawMenu: true,
    title: 'Managers',
    icon: GENERAL_ICONS.MANAGER,
    checkActive: ({ auth }) => !!auth && (auth.isClinic || (auth.isManager && auth.managerAdmin)),
    element: (
      <MainContainer>
        <Manager />
      </MainContainer>
    )
  },
  ADD_MANAGERS_PAGE: {
    name: 'managers_page',
    path: '/managers/add',
    checkActive: (a) => permissions.ACTIONS.SETTING_MANAGER_ADD_BUTTON.checkActive(a),
    element: (
      <MainContainer>
        <AddManager />
      </MainContainer>
    )
  },
  MANAGERS_LOCATIONS_PAGE: {
    name: 'managers_locations_page',
    path: '/managers/:id/locations',
    match: (id) => `/managers/${id}/locations`,
    checkActive: ({ auth }) => !!auth && (auth.isClinic || (auth.isManager && auth.managerAdmin)),
    element: (
      <MainContainer>
        <ManagerLocations />
      </MainContainer>
    )
  }
};

for (let k in settings_pages) {
  pages[k] = {
    ...settings_pages[k],
    element: <MainContainer isSettings={true}>{settings_pages[k].element}</MainContainer>
  };
}

export default pages;
